import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Check, Star, Zap } from 'lucide-react';
import { bundles } from '../data/products';

const Bundles: React.FC = () => {


  return (
    <section id="bundles" className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Unsere Streaming-Setup Empfehlungen
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-6">
            Von Einsteiger bis Profi - wir empfehlen dir die besten Equipment-Kombinationen
          </p>
          <div className="bg-dark-800/50 border border-primary-500/20 rounded-lg p-4 max-w-3xl mx-auto">
            <p className="text-sm text-gray-400">
              <strong className="text-primary-400">Hinweis:</strong> Diese Seite enthält Affiliate-Links (z. B. Amazon, Thomann und weitere Partner). 
              Wenn du über einen unserer Links einkaufst, erhalten wir ggf. eine kleine Provision – für dich entstehen keine zusätzlichen Kosten. 
              Wir empfehlen nur Produkte, die wir selbst getestet haben oder von denen wir überzeugt sind.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {bundles.map((bundle, index) => (
            <motion.a
              key={bundle.id}
              href={bundle.amazonRef}
              target="_blank"
              rel="noopener noreferrer"
              className={`card relative block flex flex-col ${bundle.popular ? 'ring-2 ring-primary-500 glow' : ''} hover:scale-105 transition-all duration-200`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              {/* Badge */}
              {bundle.badge && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    {bundle.badge}
                  </span>
                </div>
              )}

              {/* Popular Badge */}
              {bundle.popular && (
                <div className="absolute -top-3 -right-3">
                  <div className="bg-gradient-to-r from-secondary-500 to-accent-500 text-white p-2 rounded-full">
                    <Star size={16} fill="currentColor" />
                  </div>
                </div>
              )}

              {/* Top Content */}
              <div className="flex-grow">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {bundle.name}
                  </h3>
                  <p className="text-gray-300 mb-4">
                    {bundle.description}
                  </p>
                </div>

                {/* Features */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-white mb-4">
                    Was ist enthalten:
                  </h4>
                  <ul className="space-y-2">
                    {bundle.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-gray-300">
                        <Check size={16} className="text-accent-500 mr-2 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Bottom Content - Fixed at bottom */}
              <div className="mt-auto">
                {/* Setup & Support Info */}
                <div className="mb-4 p-4 bg-dark-800/50 rounded-lg">
                  <div className="flex items-center justify-center text-sm">
                    <div className="flex items-center text-accent-500">
                      <Check size={16} className="mr-1" />
                      <span>Wir helfen dir gerne beim Setup und der Einrichtung</span>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="flex items-center justify-center">
                  <div className="flex items-center text-primary-500 hover:text-primary-400 transition-colors">
                    <ExternalLink size={20} className="mr-2" />
                    <span className="font-semibold">{bundle.amazonRef.includes('thmn.to') || bundle.amazonRef.includes('thomann') ? 'Bei Thomann bestellen' : 'Bei Amazon ansehen'}</span>
                  </div>
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Brauchst du Hilfe bei der Auswahl?
          </h3>
          <p className="text-xl text-gray-300 mb-8">
            Kontaktiere uns für eine persönliche Beratung zu deinem perfekten Streaming-Setup
          </p>
          <a 
            href="https://wa.me/493093683026?text=Hallo%20tech4.stream!%20Ich%20interessiere%20mich%20für%20ein%20Streaming-Setup%20und%20brauche%20Beratung.%20Könnt%20ihr%20mir%20helfen?"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-lg px-8 py-4 glow-hover"
          >
            <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
            </svg>
            Beratung anfragen
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Bundles;
